import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeftIcon as ArrowLeft, DownloadSimpleIcon as DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Fullscreen PDF viewer rendered as a MODAL OVERLAY (portal), not a route.
 *
 * Why a modal instead of a separate `/pdf-viewer?src=...` route?
 *
 *   1. iOS Safari (especially inside an installed PWA running in
 *      `display-mode: standalone`) has flaky history-stack behaviour.
 *      Tapping "Done" on a route-based viewer often does nothing or
 *      lands the user on the wrong page. With a portal-based modal we
 *      never touch the history stack — open = mount, close = unmount.
 *
 *   2. iOS Quick Look ("Open in Preview / Files") used to intercept
 *      certificate links because the browser saw a URL that responded
 *      with `application/pdf`. We never expose the PDF URL to the
 *      browser's native handler:
 *         a. We fetch the PDF as a Blob via window.fetch (the URL
 *            already carries `?auth=TOKEN` so no Authorization header
 *            is required).
 *         b. pdfjs-dist (legacy build) renders the in-memory bytes
 *            page-by-page into <canvas>. iOS only ever sees pixels.
 *         c. The Download button uses an `<a>` pointing at a
 *            blob: URL, which iOS treats as a regular file save —
 *            no Quick Look hand-off.
 *
 * Props:
 *   - open         (bool)   — controls visibility.
 *   - onClose      (fn)     — fires when the user dismisses.
 *   - src          (string) — full PDF URL (may include ?auth=TOKEN).
 *   - title        (string) — header label.
 *   - downloadName (string) — suggested filename for Download.
 *
 * The component does its own scroll-locking on body and traps focus
 * inside the dialog while open (basic, no full focus-trap library).
 */
export default function PdfModal({ open, onClose, src, title = "Document", downloadName = "" }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState("");
  const [blobUrl, setBlobUrl] = useState("");

  // Lock body scroll while the modal is open. Restored on close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape so desktop users have a keyboard out.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Fetch + render whenever (open && src) changes.
  useEffect(() => {
    if (!open || !src) return;
    let cancelled = false;
    let loadingTask = null;
    let createdBlobUrl = "";

    setStatus("loading");
    setErrorMsg("");
    setBlobUrl("");

    (async () => {
      try {
        // 1. Pull bytes through a single authenticated fetch — keeps
        //    the PDF in JS memory and out of the browser's native
        //    PDF handler (the iOS Quick Look trap).
        const res = await fetch(src, { credentials: "omit" });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status} fetching the document.`);
        }
        const buf = await res.arrayBuffer();
        if (cancelled) return;

        // 2. Make a blob: URL for the Download button. We never link
        //    directly to `src` because iOS turns that into a Quick
        //    Look hand-off.
        const blob = new Blob([buf], { type: "application/pdf" });
        createdBlobUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(createdBlobUrl);

        // 3. Render every page to a <canvas>.
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = "/pdfjs/pdf.worker.min.js";

        // Pass the in-memory bytes directly — pdfjs never re-fetches
        // the URL, so a slow/auth-finicky network only bites us once.
        loadingTask = pdfjs.getDocument({ data: new Uint8Array(buf) });
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";

        const dpr = window.devicePixelRatio || 1;
        const cssWidth = Math.min((container.clientWidth || 720) - 32, 920);

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) break;
          const page = await pdf.getPage(i);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = cssWidth / baseViewport.width;
          const scaled = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.style.cssText =
            "display:block;margin:14px auto;box-shadow:0 8px 32px rgba(0,0,0,0.18);border-radius:6px;background:#fff;";
          canvas.style.width = `${scaled.width}px`;
          canvas.style.height = `${scaled.height}px`;
          canvas.width = Math.floor(scaled.width * dpr);
          canvas.height = Math.floor(scaled.height * dpr);
          const ctx = canvas.getContext("2d");
          ctx.scale(dpr, dpr);
          container.appendChild(canvas);
          await page.render({ canvasContext: ctx, viewport: scaled }).promise;
        }

        if (!cancelled) setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        if (e && e.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.warn("PdfModal render failed", e);
        setStatus("error");
        setErrorMsg((e && e.message) || "Could not render the document.");
      }
    })();

    return () => {
      cancelled = true;
      try { loadingTask && loadingTask.destroy && loadingTask.destroy(); } catch (_e) { /* ignore */ }
      if (createdBlobUrl) {
        // Slight delay so an in-flight Download click can still resolve
        // before the blob URL is revoked.
        setTimeout(() => URL.revokeObjectURL(createdBlobUrl), 30000);
      }
    };
  }, [open, src]);

  if (!open) return null;

  const onDownloadClick = (e) => {
    if (!blobUrl) {
      e.preventDefault();
      toast.info("Still loading — try again in a second.");
    }
    // Otherwise the <a download> takes over with the blob URL.
  };

  // Render into <body> so no parent stacking context can clip us.
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-[#0E0E0E] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="pdf-modal"
    >
      <header
        className="flex-shrink-0 bg-[#0E0E0E] border-b border-white/10 px-3 py-2 flex items-center gap-2"
        style={{ paddingTop: "max(0.5rem, env(safe-area-inset-top))" }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onClose?.()}
          className="rounded-full h-9 px-3 text-white hover:bg-white/10 hover:text-white"
          data-testid="pdf-modal-close-btn"
          aria-label="Close the document"
        >
          <ArrowLeft size={18} weight="bold" className="mr-1.5" />
          <span className="hidden sm:inline">Back</span>
          <span className="sm:hidden">Done</span>
        </Button>
        <p className="flex-1 text-white text-sm font-semibold truncate px-1">
          {title}
        </p>
        <a
          href={blobUrl || "#"}
          download={downloadName || "document.pdf"}
          onClick={onDownloadClick}
          className={`inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${
            blobUrl
              ? "text-white/90 hover:text-white hover:bg-white/10"
              : "text-white/40 cursor-not-allowed"
          }`}
          aria-label="Download PDF"
          data-testid="pdf-modal-download-btn"
        >
          <DownloadSimple size={14} weight="bold" />
          <span className="hidden sm:inline">Download</span>
        </a>
      </header>

      <div
        className="flex-1 overflow-y-auto bg-[#1F1F23]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {status === "loading" && (
          <div className="h-full flex items-center justify-center text-white/70" data-testid="pdf-modal-loading">
            <div className="text-center">
              <div className="inline-block h-7 w-7 border-2 border-white/30 border-t-white rounded-full animate-spin mb-3" />
              <p className="text-sm">Preparing your document…</p>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="h-full flex items-center justify-center text-white/80 px-6" data-testid="pdf-modal-error">
            <div className="text-center max-w-md">
              <p className="font-semibold mb-2">We couldn&apos;t open this document inline.</p>
              <p className="text-sm text-white/60 mb-4">{errorMsg}</p>
              {blobUrl && (
                <a
                  href={blobUrl}
                  download={downloadName || "document.pdf"}
                  className="inline-flex items-center gap-1.5 bg-white text-ink rounded-full px-4 py-2 text-sm font-semibold"
                  data-testid="pdf-modal-fallback-download"
                >
                  <DownloadSimple size={14} weight="bold" />
                  Download instead
                </a>
              )}
            </div>
          </div>
        )}
        <div
          ref={containerRef}
          className={status === "ready" ? "px-4 py-3" : "hidden"}
          data-testid="pdf-modal-canvas-container"
        />
      </div>
    </div>,
    document.body,
  );
}
